/**
 * Substituição local de @mui/icons-material — usa MDI via Icon/iconMap.
 * Gerado a partir dos imports em src/ (171 ícones).
 */
import React from 'react';
import Icon from './components/ui/Icon';

/** Converte PascalCase MUI → kebab-case (chaves do iconMap). */
export function muiNameToKebab(name) {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
}

/** Overrides quando o kebab-case direto não existe no iconMap. */
export const MUI_ICON_ALIASES = {
  AccessTime: 'access-time',
  AccountBalance: 'cash',
  Android: 'phone-android',
  Article: 'description',
  Assignment: 'clipboard-list',
  AutoGraph: 'analytics',
  Badge: 'certificate',
  BlockOutlined: 'block',
  Calculate: 'scale',
  CalendarToday: 'calendar',
  CheckBox: 'check-circle',
  CheckBoxOutlineBlank: 'check',
  ChildCare: 'account',
  Circle: 'info',
  EmojiEvents: 'trophy',
  Fingerprint: 'key',
  FoodBank: 'food',
  GetApp: 'download',
  HealthAndSafety: 'shield-check',
  HourglassTop: 'schedule',
  ImageSearch: 'search',
  Insights: 'analytics',
  Language: 'web',
  Layers: 'category',
  LinkOff: 'block',
  LocalDining: 'restaurant',
  LockReset: 'key-variant',
  MenuBook: 'book',
  MonetizationOn: 'currency-usd',
  NewReleases: 'rocket',
  Notes: 'clipboard-list',
  Payments: 'credit-card',
  PersonSearch: 'account-search',
  Pix: 'key',
  PriorityHigh: 'warning',
  Psychology: 'brain',
  Repeat: 'refresh',
  Share: 'open-in-new',
  Sms: 'send',
  SupervisedUserCircle: 'account-cog',
  Timeline: 'trending-up',
  TouchApp: 'cellphone-check',
  TuneOutlined: 'filter',
  Unsubscribe: 'block',
  Update: 'refresh',
  VerifiedUser: 'shield-check',
  ViewList: 'clipboard-list',
  Workspace: 'business-center',
};

function resolveSize({ fontSize, sx }) {
  const raw = fontSize ?? sx?.fontSize ?? 24;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') {
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? 24 : parsed;
  }
  return 24;
}

/**
 * Factory compatível com API de ícones MUI.
 * @param {string} muiName - Nome exportado pelo MUI (ex: LocalFireDepartment)
 * @param {string} [iconName] - Nome kebab no iconMap; default: alias ou muiNameToKebab(muiName)
 */
export function createMuiIcon(muiName, iconName) {
  const resolvedName =
    iconName ?? MUI_ICON_ALIASES[muiName] ?? muiNameToKebab(muiName);

  const MuiIconComponent = ({ sx, fontSize, className, style, color, ...rest }) => {
    const size = resolveSize({ fontSize, sx });
    const resolvedColor = color ?? sx?.color ?? style?.color ?? 'currentColor';
    const mergedStyle = {
      ...style,
      ...(sx?.opacity != null ? { opacity: sx.opacity } : {}),
      ...(sx?.marginBottom != null ? { marginBottom: sx.marginBottom } : {}),
    };

    return (
      <Icon
        name={resolvedName}
        size={size}
        color={resolvedColor}
        className={className}
        style={Object.keys(mergedStyle).length ? mergedStyle : undefined}
        {...rest}
      />
    );
  };

  MuiIconComponent.displayName = muiName;
  MuiIconComponent.muiName = muiName;
  MuiIconComponent.iconName = resolvedName;

  return MuiIconComponent;
}

export const AccessTime = createMuiIcon('AccessTime');
export const AccountBalance = createMuiIcon('AccountBalance', 'cash');
export const AccountCircle = createMuiIcon('AccountCircle');
export const Add = createMuiIcon('Add');
export const AdminPanelSettings = createMuiIcon('AdminPanelSettings');
export const Analytics = createMuiIcon('Analytics');
export const Android = createMuiIcon('Android', 'phone-android');
export const Apple = createMuiIcon('Apple');
export const ArrowBack = createMuiIcon('ArrowBack');
export const Article = createMuiIcon('Article', 'description');
export const Assignment = createMuiIcon('Assignment', 'clipboard-list');
export const AttachMoney = createMuiIcon('AttachMoney');
export const AutoGraph = createMuiIcon('AutoGraph', 'analytics');
export const Badge = createMuiIcon('Badge', 'certificate');
export const BakeryDining = createMuiIcon('BakeryDining');
export const BarChart = createMuiIcon('BarChart');
export const Blender = createMuiIcon('Blender');
export const Block = createMuiIcon('Block');
export const BlockOutlined = createMuiIcon('BlockOutlined', 'block');
export const BreakfastDining = createMuiIcon('BreakfastDining');
export const Business = createMuiIcon('Business');
export const BusinessCenter = createMuiIcon('BusinessCenter');
export const Cake = createMuiIcon('Cake');
export const Calculate = createMuiIcon('Calculate', 'scale');
export const CalendarMonth = createMuiIcon('CalendarMonth');
export const CalendarToday = createMuiIcon('CalendarToday', 'calendar');
export const Camera = createMuiIcon('Camera');
export const Campaign = createMuiIcon('Campaign');
export const Cancel = createMuiIcon('Cancel');
export const Category = createMuiIcon('Category');
export const Check = createMuiIcon('Check');
export const CheckBox = createMuiIcon('CheckBox', 'check-circle');
export const CheckBoxOutlineBlank = createMuiIcon('CheckBoxOutlineBlank', 'check');
export const CheckCircle = createMuiIcon('CheckCircle');
export const ChildCare = createMuiIcon('ChildCare', 'account');
export const Circle = createMuiIcon('Circle', 'info');
export const Close = createMuiIcon('Close');
export const CloudUpload = createMuiIcon('CloudUpload');
export const Coffee = createMuiIcon('Coffee');
export const ContentCopy = createMuiIcon('ContentCopy');
export const Cookie = createMuiIcon('Cookie');
export const CreditCard = createMuiIcon('CreditCard');
export const Dashboard = createMuiIcon('Dashboard');
export const Delete = createMuiIcon('Delete');
export const Description = createMuiIcon('Description');
export const DinnerDining = createMuiIcon('DinnerDining');
export const Download = createMuiIcon('Download');
export const Edit = createMuiIcon('Edit');
export const Egg = createMuiIcon('Egg');
export const EggAlt = createMuiIcon('EggAlt');
export const Email = createMuiIcon('Email');
export const EmojiEvents = createMuiIcon('EmojiEvents', 'trophy');
export const EmojiFoodBeverage = createMuiIcon('EmojiFoodBeverage');
export const EnergySavingsLeaf = createMuiIcon('EnergySavingsLeaf');
export const Error = createMuiIcon('Error');
export const ExpandLess = createMuiIcon('ExpandLess');
export const ExpandMore = createMuiIcon('ExpandMore');
export const Facebook = createMuiIcon('Facebook');
export const Fastfood = createMuiIcon('Fastfood');
export const Favorite = createMuiIcon('Favorite');
export const FilterList = createMuiIcon('FilterList');
export const FilterVintage = createMuiIcon('FilterVintage');
export const Fingerprint = createMuiIcon('Fingerprint', 'key');
export const FitnessCenter = createMuiIcon('FitnessCenter');
export const Flag = createMuiIcon('Flag');
export const FoodBank = createMuiIcon('FoodBank', 'food');
export const Gavel = createMuiIcon('Gavel');
export const GetApp = createMuiIcon('GetApp', 'download');
export const Grain = createMuiIcon('Grain');
export const Grass = createMuiIcon('Grass');
export const Group = createMuiIcon('Group');
export const Groups = createMuiIcon('Groups');
export const Handshake = createMuiIcon('Handshake');
export const HealthAndSafety = createMuiIcon('HealthAndSafety', 'shield-check');
export const HelpOutline = createMuiIcon('HelpOutline');
export const History = createMuiIcon('History');
export const Home = createMuiIcon('Home');
export const HourglassTop = createMuiIcon('HourglassTop', 'schedule');
export const Icecream = createMuiIcon('Icecream');
export const ImageSearch = createMuiIcon('ImageSearch', 'search');
export const Info = createMuiIcon('Info');
export const Insights = createMuiIcon('Insights', 'analytics');
export const Instagram = createMuiIcon('Instagram');
export const Inventory = createMuiIcon('Inventory');
export const KebabDining = createMuiIcon('KebabDining');
export const Kitchen = createMuiIcon('Kitchen');
export const Language = createMuiIcon('Language', 'web');
export const Layers = createMuiIcon('Layers', 'category');
export const Link = createMuiIcon('Link');
export const LinkOff = createMuiIcon('LinkOff', 'block');
export const Liquor = createMuiIcon('Liquor');
export const LocalBar = createMuiIcon('LocalBar');
export const LocalCafe = createMuiIcon('LocalCafe');
export const LocalDining = createMuiIcon('LocalDining', 'restaurant');
export const LocalDrink = createMuiIcon('LocalDrink');
export const LocalFireDepartment = createMuiIcon('LocalFireDepartment');
export const LocalFlorist = createMuiIcon('LocalFlorist');
export const LocalOffer = createMuiIcon('LocalOffer');
export const LocalPharmacy = createMuiIcon('LocalPharmacy');
export const LocalPizza = createMuiIcon('LocalPizza');
export const Lock = createMuiIcon('Lock');
export const LockReset = createMuiIcon('LockReset', 'key-variant');
export const Logout = createMuiIcon('Logout');
export const LunchDining = createMuiIcon('LunchDining');
export const Medication = createMuiIcon('Medication');
export const Menu = createMuiIcon('Menu');
export const MenuBook = createMuiIcon('MenuBook', 'book');
export const MonetizationOn = createMuiIcon('MonetizationOn', 'currency-usd');
export const Nature = createMuiIcon('Nature');
export const NavigateNext = createMuiIcon('NavigateNext');
export const NewReleases = createMuiIcon('NewReleases', 'rocket');
export const Notes = createMuiIcon('Notes', 'clipboard-list');
export const Notifications = createMuiIcon('Notifications');
export const Opacity = createMuiIcon('Opacity', 'oil');
export const OpenInNew = createMuiIcon('OpenInNew');
export const Palette = createMuiIcon('Palette');
export const Park = createMuiIcon('Park');
export const Payments = createMuiIcon('Payments', 'credit-card');
export const People = createMuiIcon('People');
export const Percent = createMuiIcon('Percent');
export const Person = createMuiIcon('Person');
export const PersonAdd = createMuiIcon('PersonAdd');
export const PersonOff = createMuiIcon('PersonOff');
export const PersonSearch = createMuiIcon('PersonSearch', 'account-search');
export const Phone = createMuiIcon('Phone');
export const PhoneAndroid = createMuiIcon('PhoneAndroid');
export const PhoneIphone = createMuiIcon('PhoneIphone');
export const Pix = createMuiIcon('Pix', 'key');
export const Poll = createMuiIcon('Poll');
export const PriorityHigh = createMuiIcon('PriorityHigh', 'warning');
export const Psychology = createMuiIcon('Psychology', 'brain');
export const QuestionMark = createMuiIcon('QuestionMark');
export const Receipt = createMuiIcon('Receipt');
export const Refresh = createMuiIcon('Refresh');
export const Repeat = createMuiIcon('Repeat', 'refresh');
export const Restaurant = createMuiIcon('Restaurant');
export const RiceBowl = createMuiIcon('RiceBowl');
export const Save = createMuiIcon('Save');
export const Scale = createMuiIcon('Scale');
export const Schedule = createMuiIcon('Schedule');
export const Science = createMuiIcon('Science');
export const Search = createMuiIcon('Search');
export const Security = createMuiIcon('Security');
export const Send = createMuiIcon('Send');
export const SetMeal = createMuiIcon('SetMeal');
export const Settings = createMuiIcon('Settings');
export const Share = createMuiIcon('Share', 'open-in-new');
export const Shield = createMuiIcon('Shield');
export const SmartToy = createMuiIcon('SmartToy');
export const Sms = createMuiIcon('Sms', 'send');
export const Spa = createMuiIcon('Spa');
export const SportsBar = createMuiIcon('SportsBar');
export const Star = createMuiIcon('Star');
export const Storage = createMuiIcon('Storage');
export const SupervisedUserCircle = createMuiIcon('SupervisedUserCircle', 'account-cog');
export const Support = createMuiIcon('Support');
export const SupportAgent = createMuiIcon('SupportAgent');
export const Timeline = createMuiIcon('Timeline', 'trending-up');
export const Today = createMuiIcon('Today');
export const TouchApp = createMuiIcon('TouchApp', 'cellphone-check');
export const TrendingUp = createMuiIcon('TrendingUp');
export const TuneOutlined = createMuiIcon('TuneOutlined', 'filter');
export const Unsubscribe = createMuiIcon('Unsubscribe', 'block');
export const Update = createMuiIcon('Update', 'refresh');
export const Verified = createMuiIcon('Verified');
export const VerifiedUser = createMuiIcon('VerifiedUser', 'shield-check');
export const ViewList = createMuiIcon('ViewList', 'clipboard-list');
export const Visibility = createMuiIcon('Visibility');
export const VisibilityOff = createMuiIcon('VisibilityOff');
export const Warning = createMuiIcon('Warning');
export const WaterDrop = createMuiIcon('WaterDrop');
export const Workspace = createMuiIcon('Workspace', 'business-center');
export const Yard = createMuiIcon('Yard');

export { Error as ErrorIcon };

const muiIcons = {
  AccessTime,
  AccountBalance,
  AccountCircle,
  Add,
  AdminPanelSettings,
  Analytics,
  Android,
  Apple,
  ArrowBack,
  Article,
  Assignment,
  AttachMoney,
  AutoGraph,
  Badge,
  BakeryDining,
  BarChart,
  Blender,
  Block,
  BlockOutlined,
  BreakfastDining,
  Business,
  BusinessCenter,
  Cake,
  Calculate,
  CalendarMonth,
  CalendarToday,
  Camera,
  Campaign,
  Cancel,
  Category,
  Check,
  CheckBox,
  CheckBoxOutlineBlank,
  CheckCircle,
  ChildCare,
  Circle,
  Close,
  CloudUpload,
  Coffee,
  ContentCopy,
  Cookie,
  CreditCard,
  Dashboard,
  Delete,
  Description,
  DinnerDining,
  Download,
  Edit,
  Egg,
  EggAlt,
  Email,
  EmojiEvents,
  EmojiFoodBeverage,
  EnergySavingsLeaf,
  Error,
  ExpandLess,
  ExpandMore,
  Facebook,
  Fastfood,
  Favorite,
  FilterList,
  FilterVintage,
  Fingerprint,
  FitnessCenter,
  Flag,
  FoodBank,
  Gavel,
  GetApp,
  Grain,
  Grass,
  Group,
  Groups,
  Handshake,
  HealthAndSafety,
  HelpOutline,
  History,
  Home,
  HourglassTop,
  Icecream,
  ImageSearch,
  Info,
  Insights,
  Instagram,
  Inventory,
  KebabDining,
  Kitchen,
  Language,
  Layers,
  Link,
  LinkOff,
  Liquor,
  LocalBar,
  LocalCafe,
  LocalDining,
  LocalDrink,
  LocalFireDepartment,
  LocalFlorist,
  LocalOffer,
  LocalPharmacy,
  LocalPizza,
  Lock,
  LockReset,
  Logout,
  LunchDining,
  Medication,
  Menu,
  MenuBook,
  MonetizationOn,
  Nature,
  NavigateNext,
  NewReleases,
  Notes,
  Notifications,
  Palette,
  Park,
  Payments,
  People,
  Percent,
  Person,
  PersonAdd,
  PersonOff,
  PersonSearch,
  Phone,
  PhoneAndroid,
  PhoneIphone,
  Pix,
  Poll,
  PriorityHigh,
  Psychology,
  QuestionMark,
  Receipt,
  Refresh,
  Repeat,
  Restaurant,
  RiceBowl,
  Save,
  Scale,
  Schedule,
  Science,
  Search,
  Security,
  Send,
  SetMeal,
  Settings,
  Share,
  Shield,
  SmartToy,
  Sms,
  Spa,
  SportsBar,
  Star,
  Storage,
  SupervisedUserCircle,
  Support,
  SupportAgent,
  Timeline,
  Today,
  TouchApp,
  TrendingUp,
  TuneOutlined,
  Unsubscribe,
  Update,
  Verified,
  VerifiedUser,
  ViewList,
  Visibility,
  VisibilityOff,
  Warning,
  WaterDrop,
  Workspace,
  Yard,
};

export default muiIcons;
